import { useState } from 'react';
import { CheckCircle2, Send, PhoneCall, Mail } from 'lucide-react';
import { WHATSAPP_LINK, SITE } from '../lib/site';

interface LeadFormProps {
  title?: string;
  subtitle?: string;
  compact?: boolean;
}

/**
 * Formulario de presupuesto — sin backend: construye el mensaje y abre
 * WhatsApp con todos los datos, o envía por email como alternativa.
 * Conversión máxima: respuesta en 24h, CTA duplicado.
 */
const LeadForm = ({ title = 'Solicita tu presupuesto gratis', subtitle = 'Responde en menos de 24 horas laborables. Sin compromiso y sin coste.', compact = false }: LeadFormProps) => {
  const [sent, setSent] = useState(false);
  const [data, setData] = useState({ name: '', phone: '', email: '', type: 'Reforma integral', message: '' });

  const update = (k: keyof typeof data) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setData({ ...data, [k]: e.target.value });

  const handleWhatsApp = (e: React.FormEvent) => {
    e.preventDefault();
    const msg =
      `Hola, soy ${data.name || '—'}.\n` +
      `Tipo de proyecto: ${data.type}.\n` +
      `Teléfono: ${data.phone || '—'}.\n` +
      (data.email ? `Email: ${data.email}.\n` : '') +
      (data.message ? `Detalles: ${data.message}` : 'Quiero recibir un presupuesto.');
    window.open(WHATSAPP_LINK(msg), '_blank', 'noopener');
    setSent(true);
    setTimeout(() => setSent(false), 6000);
  };

  const handleMailto = () => {
    const subject = `Presupuesto: ${data.type} — ${data.name || 'consulta'}`;
    const body = `Nombre: ${data.name}\nTeléfono: ${data.phone}\nEmail: ${data.email}\nTipo: ${data.type}\n\n${data.message}`;
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className={`bg-ink text-paper rounded-[4px] p-8 md:p-10 ${compact ? '' : 'shadow-[0_30px_80px_-30px_rgba(22,19,15,0.6)]'}`}>
      <h3 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl">{title}</h3>
      <p className="mt-3 text-paper/60 text-sm leading-relaxed">{subtitle}</p>

      {sent && (
        <div className="mt-6 flex items-start gap-3 bg-clay/15 border border-clay/40 rounded-[4px] p-4" role="status">
          <CheckCircle2 size={20} className="text-clay-light shrink-0 mt-0.5" />
          <p className="text-sm text-paper/90">
            ¡Gracias! Te hemos abierto WhatsApp con tu consulta preparada.
            Pulsa enviar y te responderemos en menos de 24h. También puedes llamarnos al{' '}
            <a href={`tel:${SITE.phone}`} className="text-clay-light font-semibold underline">{SITE.phoneDisplay}</a>.
          </p>
        </div>
      )}

      <form onSubmit={handleWhatsApp} className="mt-8 space-y-6">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="lead-name" className="field-label text-paper/50">Nombre *</label>
            <input id="lead-name" required className="field !border-paper/20 !text-paper placeholder:!text-paper/30" placeholder="Tu nombre" value={data.name} onChange={update('name')} />
          </div>
          <div>
            <label htmlFor="lead-phone" className="field-label text-paper/50">Teléfono *</label>
            <input id="lead-phone" required type="tel" className="field !border-paper/20 !text-paper placeholder:!text-paper/30" placeholder="+34 600 000 000" value={data.phone} onChange={update('phone')} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="lead-email" className="field-label text-paper/50">Email</label>
            <input id="lead-email" type="email" className="field !border-paper/20 !text-paper placeholder:!text-paper/30" placeholder="tu@email.com" value={data.email} onChange={update('email')} />
          </div>
          <div>
            <label htmlFor="lead-type" className="field-label text-paper/50">Tipo de proyecto</label>
            <select id="lead-type" className="field !border-paper/20 !text-paper bg-ink" value={data.type} onChange={update('type')}>
              <option>Reforma integral</option>
              <option>Reforma parcial (baño / cocina)</option>
              <option>Obra nueva</option>
              <option>Reforma comercial</option>
              <option>Rehabilitación</option>
              <option>Mantenimiento / comunidad</option>
              <option>Otro</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="lead-message" className="field-label text-paper/50">Cuéntanos tu proyecto</label>
          <textarea
            id="lead-message"
            rows={4}
            className="field !border-paper/20 !text-paper placeholder:!text-paper/30 resize-none"
            placeholder="Superficie aproximada, ubicación, estado actual, plazos…"
            value={data.message}
            onChange={update('message')}
          />
        </div>

        <button type="submit" className="btn w-full bg-clay text-white !py-4" data-size="lg">
          <Send size={17} />
          Enviar por WhatsApp
        </button>
        <div className="flex items-center gap-4 pt-1">
          <span className="h-px flex-1 bg-paper/15" aria-hidden="true" />
          <span className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-paper/40">o</span>
          <span className="h-px flex-1 bg-paper/15" aria-hidden="true" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <a href={`tel:${SITE.phone}`} className="btn border border-paper/25 text-paper !px-3">
            <PhoneCall size={15} className="text-clay-light" />
            Llamar
          </a>
          <button type="button" onClick={handleMailto} className="btn border border-paper/25 text-paper !px-3">
            <Mail size={15} className="text-clay-light" />
            Email
          </button>
        </div>
        <p className="text-[0.68rem] text-paper/35 leading-relaxed text-center pt-1">
          Al enviar aceptas nuestra política de privacidad. Nunca compartimos tus datos.
        </p>
      </form>
    </div>
  );
};

export default LeadForm;
